import { useState, useEffect } from "react";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import useTranslation from "@babackapp/translations/use-translation";
import { CarrierDto } from "@babackapp/private-api-client/types";
import {
  extractCarrierTagValue,
  createCarrierTagCondition,
} from "@/pages/carriers/carriers-informations/lib/tag-conditions.helpers";
import { useUpdateCarrierConditionalRules } from "@babackapp/private-api-client/client/use-update-carrier-conditional-rules";
import { Trash2 } from "lucide-react";

interface TagConditionModalProps {
  carrier: CarrierDto | null;
  shopId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const TagConditionModal = ({
  carrier,
  shopId,
  isOpen,
  onClose,
}: TagConditionModalProps) => {
  const t = useTranslation();
  const updateCarrierConditionalRules =
    useUpdateCarrierConditionalRules(shopId);
  const [tagValue, setTagValue] = useState("");

  const currentTag = carrier
    ? extractCarrierTagValue(carrier.conditionalRules)
    : null;

  useEffect(() => {
    if (carrier) {
      setTagValue(currentTag || "");
    }
  }, [carrier, currentTag]);

  const handleSave = () => {
    if (!carrier) return;

    const trimmedTag = tagValue.trim();
    if (trimmedTag) {
      const newConditionalRules = createCarrierTagCondition(trimmedTag);
      updateCarrierConditionalRules.mutate(
        {
          carrierId: carrier.id,
          conditionalRules: newConditionalRules,
        },
        {
          onSuccess: () => onClose(),
        },
      );
    }
  };

  const handleRemove = () => {
    if (!carrier) return;

    updateCarrierConditionalRules.mutate(
      {
        carrierId: carrier.id,
        conditionalRules: null,
      },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  const handleClose = () => {
    setTagValue("");
    onClose();
  };

  if (!carrier) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("carrier.tagCondition.title", {
              carrier: carrier.code.toUpperCase(),
            })}
          </DialogTitle>
          <DialogDescription className="space-y-2">
            {/*<div>*/}
            {/*  {t("carrier.tagCondition.description")}*/}
            {/*</div>*/}
            <div className="text-xs text-muted-foreground">
              {t("carrier.tagCondition.example")}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tag-input">{t("carrier.tagCondition.label")}</Label>
            <Input
              id="tag-input"
              placeholder={t("carrier.tagCondition.placeholder")}
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleClose();
              }}
            />
          </div>

          <div className="flex justify-between">
            <div>
              {currentTag && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemove}
                  disabled={updateCarrierConditionalRules.isPending}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {t("carrier.tagCondition.remove")}
                </Button>
              )}
            </div>

            <div className="flex space-x-2">
              <Button variant="outline" onClick={handleClose}>
                {t("misc.cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  !tagValue.trim() || updateCarrierConditionalRules.isPending
                }
              >
                {updateCarrierConditionalRules.isPending
                  ? t("carrier.tagCondition.saving")
                  : t("misc.save")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
